import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { recentTokenKey } from 'hooks/useRecentTokens'
import { TokenMarketStats } from 'hooks/useTrendingTokens'
import { useMemo } from 'react'
import { Briefcase, Clock, TrendingUp } from 'react-feather'

import { CurrencyListItem, SectionHeaderItem } from './CurrencyList'

// Holdings worth less than this are dust and stay out of "Your tokens".
const MIN_HOLDING_USD = 0.01
const TRENDING_COUNT = 50

const SECTIONS = {
  yourTokens: { kind: 'section-header', key: 'your-tokens', title: 'Your tokens', icon: Briefcase },
  recent: { kind: 'section-header', key: 'recent', title: 'Recent', icon: Clock },
  trending: { kind: 'section-header', key: 'trending', title: 'Trending tokens', icon: TrendingUp },
  tokens: { kind: 'section-header', key: 'tokens', title: 'Tokens' },
} as const satisfies Record<string, SectionHeaderItem>

type Balances = { [tokenAddress: string]: CurrencyAmount<Token> | undefined }

export type TokenSelectorItemsParams = {
  query: string
  /** Tokens passing the selected category filters and the query, native included when it matches. */
  pool: Currency[]
  nativeBalance?: CurrencyAmount<Currency>
  balances: Balances
  prices: { [address: string]: number | null }
  marketStats: ReadonlyMap<string, TokenMarketStats>
  /** Trending rank (1 = hottest) by lowercase address. */
  trendingRanks: ReadonlyMap<string, number>
  /** While true, the fallback "Tokens" section is held back instead of flashing. */
  trendingLoading: boolean
  recentKeys: readonly string[]
  onClearRecent: () => void
}

/**
 * Sections of the token selector, all drawn from the filtered pool. Without a
 * query: the user's tokens, recent picks and trending tokens; with one: the
 * user's matches and the other matches.
 */
export function useTokenSelectorItems({
  query,
  pool,
  nativeBalance,
  balances,
  prices,
  marketStats,
  trendingRanks,
  trendingLoading,
  recentKeys,
  onClearRecent,
}: TokenSelectorItemsParams): CurrencyListItem[] {
  return useMemo(() => {
    const balanceOf = (currency: Currency) => (currency.isNative ? nativeBalance : balances[currency.address])
    const usdValue = (currency: Currency) => {
      const balance = balanceOf(currency)
      const price = prices[currency.wrapped.address]
      return balance && price ? Number(balance.toExact()) * price : null
    }
    const isHeld = (currency: Currency) => {
      if (!balanceOf(currency)?.greaterThan(0)) return false
      const value = usdValue(currency)
      return value === null || value >= MIN_HOLDING_USD
    }
    const byValue = (a: Currency, b: Currency) => (usdValue(b) ?? 0) - (usdValue(a) ?? 0)
    const volumeOf = (currency: Currency) => marketStats.get(currency.wrapped.address.toLowerCase())?.volume24hUsd ?? 0

    const held = pool.filter(isHeld).sort(byValue)
    const heldKeys = new Set(held.map(recentTokenKey))
    const notHeld = pool.filter((currency) => !heldKeys.has(recentTokenKey(currency)))

    const trimmedQuery = query.trim()
    if (trimmedQuery) {
      const others = rankByQuery(
        trimmedQuery,
        [...notHeld].sort((a, b) => volumeOf(b) - volumeOf(a))
      )
      return [...section(SECTIONS.yourTokens, held), ...section(SECTIONS.tokens, others)]
    }

    const byKey = new Map(notHeld.map((currency) => [recentTokenKey(currency), currency]))
    const recent = recentKeys.flatMap((key) => byKey.get(key) ?? [])
    const ranked = rankByTrending(notHeld, trendingRanks)
    const rankedKeys = new Set(ranked.map(recentTokenKey))
    // The ranking is mostly memecoins, so tabs like RWA hold few of them; the busiest
    // remaining tokens of the tab fill the list up.
    const trendingTokens = [
      ...ranked,
      ...rankByVolume(
        notHeld.filter((currency) => !rankedKeys.has(recentTokenKey(currency))),
        volumeOf
      ),
    ].slice(0, TRENDING_COUNT)

    return [
      ...section(SECTIONS.yourTokens, held),
      ...section({ ...SECTIONS.recent, action: { label: 'Clear', onClick: onClearRecent } }, recent),
      ...(trendingTokens.length > 0
        ? section(SECTIONS.trending, trendingTokens)
        : trendingLoading
        ? []
        : section(SECTIONS.tokens, notHeld)),
    ]
  }, [
    query,
    pool,
    nativeBalance,
    balances,
    prices,
    marketStats,
    trendingRanks,
    trendingLoading,
    recentKeys,
    onClearRecent,
  ])
}

function rankByTrending(currencies: Currency[], ranks: ReadonlyMap<string, number>): Currency[] {
  return currencies
    .flatMap((currency) => {
      const rank = ranks.get(currency.wrapped.address.toLowerCase())
      return rank === undefined ? [] : [{ currency, rank }]
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ currency }) => currency)
}

function rankByVolume(currencies: Currency[], volumeOf: (currency: Currency) => number): Currency[] {
  return currencies.filter((currency) => volumeOf(currency) > 0).sort((a, b) => volumeOf(b) - volumeOf(a))
}

function section(header: SectionHeaderItem, currencies: Currency[]): CurrencyListItem[] {
  return currencies.length > 0 ? [header, ...currencies] : []
}

/** Exact symbol matches first, then symbol prefixes, keeping the given order within each. */
function rankByQuery(query: string, currencies: Currency[]): Currency[] {
  const q = query.toLowerCase()
  const rank = (currency: Currency) => {
    const symbol = currency.symbol?.toLowerCase() ?? ''
    if (symbol === q) return 0
    if (symbol.startsWith(q)) return 1
    return 2
  }
  return currencies
    .map((currency, index) => ({ currency, index, rank: rank(currency) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ currency }) => currency)
}
